import { CarOutlined } from '@ant-design/icons';
import { Descriptions, Space, Table, Typography, message } from 'antd';
import axios from 'axios';
import { useContext, useState } from 'react';
import { BsBox2 } from 'react-icons/bs';
import { IoWarningOutline } from 'react-icons/io5';
import { MdDateRange } from 'react-icons/md';
import { useHistory } from 'react-router-dom';

import ProductImage from '../../components/productImage';
import SectionCard from '../../components/sectionCard/SectionCard';
import StepFooter from '../../components/stepFooter/StepFooter';
import { isHandledByInterceptor } from '../../utils/apiError';
import { dateTimeISO, formatCurrency } from '../../utils/format';
import { AddOrderContext } from './AddOrderContext';
import { clearDraft } from './draft';

const { Text } = Typography;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 40;

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */
const TITLE_MAX_WIDTH = 320;

/** Хоосон утгыг "-" болгож харуулна */
const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

const IMAGE_SHADOW = '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)';

/** Descriptions-ийн ерөнхий тохиргоо: label дээр, value доор, зай багатай */
const descriptionProps = {
  size: 'small',
  colon: false,
  layout: 'vertical',
  column: { xs: 1, sm: 2 },
  labelStyle: { paddingBottom: 0 },
  contentStyle: { paddingBottom: 8 },
};

/** Тоог нийлбэрт нэмэхэд NaN гарахаас хамгаална */
const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const columns = [
  {
    key: 'index',
    title: '№',
    width: 48,
    align: 'center',
    render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
  },
  {
    key: 'product',
    title: 'Бараа',
    render: (_, record) => (
      <Space size={12} align="center">
        <ProductImage
          src={record?.imageCover}
          alt={record?.title ?? 'Барааны зураг'}
          size={IMAGE_SIZE}
          style={{
            objectFit: 'cover',
            borderRadius: 6,
            boxShadow: IMAGE_SHADOW,
          }}
        />
        <Space direction="vertical" size={0} style={{ maxWidth: TITLE_MAX_WIDTH }}>
          <Text style={{ maxWidth: TITLE_MAX_WIDTH }} ellipsis={{ tooltip: record?.title }}>
            {val(record?.title)}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
            {record?.productSku}
          </Text>
        </Space>
      </Space>
    ),
  },
  {
    key: 'price',
    title: 'Нэгжийн үнэ',
    align: 'right',
    width: 140,
    render: (_, record) => formatCurrency(num(record?.price)),
  },
  {
    key: 'stock',
    title: 'Үлдэгдэл',
    align: 'center',
    width: 120,
    render: (_, record) => (record?.stock != null ? `${record.stock} ш` : '-'),
  },
  {
    key: 'totalUnit',
    title: 'Тоо ширхэг',
    align: 'center',
    width: 120,
    render: (_, record) => `${num(record?.totalUnit)} ш`,
  },
  {
    key: 'total',
    title: 'Нийт дүн',
    align: 'center',
    width: 160,
    render: (_, record) => (
      <Text strong>{formatCurrency(num(record?.price) * num(record?.totalUnit))}</Text>
    ),
  },
];

/** Хүснэгтийн нийлбэр мөр — тоо ширхэг, нийт дүн */
const renderSummary = (rows = []) => {
  const totalUnit = rows.reduce((total, record) => total + num(record?.totalUnit), 0);
  const totalPrice = rows.reduce(
    (total, record) => total + num(record?.totalUnit) * num(record?.price),
    0
  );

  return (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0} colSpan={4} align="right">
        <Text type="secondary">Нийт</Text>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={4} align="center">
        <Text strong>{`${totalUnit} ш`}</Text>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={5} align="center">
        <Text strong>{formatCurrency(totalPrice)}</Text>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );
};

const Step3 = ({ prevStep }) => {
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);

  const {
    draftKey,
    branchCode,
    products,
    finalProductList,
    deliveryDateFinal,
    deliveryTimeFinal,
    description,
    importantNotes,
    carPrice,
    totalWeight,
    totalVolume,
    pricingCode,
    procurementType,
    field,
  } = useContext(AddOrderContext) ?? {};

  const isProcurement = procurementType === 'procurement';
  const branchDetail = field?.branches?.detail?.[branchCode];
  const pricingDetail = Object.values(field?.pricing?.detail ?? {});
  const transportPrice = isProcurement ? num(carPrice) : 0;

  const handleSubmit = async () => {
    const payload = {
      branchCode,
      pickupDate: dateTimeISO(deliveryDateFinal, deliveryTimeFinal),
      procurementType,
      importantNotes: importantNotes || [],
      description,
      pricingCode,
      totalWeight,
      totalVolume,
      products,
      dropoffDate: dateTimeISO(deliveryDateFinal, deliveryTimeFinal),
    };

    setSubmitting(true);
    try {
      const { data } = await axios.post('/api/v1/request/create-procurement-request', payload);

      if (data.statusCode === 200) {
        message.success('Захиалга амжилттай үүслээ.');
        // Дараагийн захиалгад хагас дутуу мэдээлэл нь сэргэхгүй байхын тулд
        clearDraft(draftKey);
        history.push('/merchant/order/pick');
        return;
      }

      // Interceptor мэдэгдээгүй кодод л локал мэдэгдэл гаргана — эс тэгвээс давхардана
      if (!isHandledByInterceptor(data)) {
        // Серверийн тайлбар урт байж болох тул уншиж амжихаар удаан харуулна
        message.error(data?.message_mn ?? data?.message ?? 'Захиалга үүсгэхэд алдаа гарлаа.', 6);
      }
    } catch (e) {
      // Мэдэгдлийг axios-ийн global interceptor гаргана — энд давхардуулахгүй
      console.error('create-procurement-request: ', e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <SectionCard icon={<BsBox2 />} title="Бараа татах агуулах" styles={{ body: { padding: 0 } }}>
        <Descriptions
          {...descriptionProps}
          style={{ padding: '12px 16px 0' }}
          items={[
            { key: 'name', label: 'Нэр', children: val(branchDetail?.name ?? branchCode) },
            {
              key: 'address',
              label: 'Дэлгэрэнгүй хаяг',
              children: val(branchDetail?.addressName),
            },
          ]}
        />
        <div style={{ padding: 10 }}>
          <Table
            columns={columns}
            dataSource={finalProductList}
            rowKey={(record, index) => record?._id ?? index}
            size="middle"
            pagination={false}
            summary={renderSummary}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </SectionCard>

      <SectionCard icon={<BsBox2 />} title="Овор хэмжээ">
        <Descriptions
          {...descriptionProps}
          items={[
            { key: 'weight', label: 'Жин (кг)', children: val(totalWeight) },
            { key: 'volume', label: 'Эзэлхүүн (м³)', children: val(totalVolume) },
          ]}
        />
      </SectionCard>

      <SectionCard icon={<MdDateRange />} title="Хүлээж авах огноо">
        <Descriptions
          {...descriptionProps}
          items={[
            { key: 'date', label: 'Авах огноо', children: val(deliveryDateFinal) },
            { key: 'time', label: 'Авах цаг', children: val(deliveryTimeFinal) },
          ]}
        />
      </SectionCard>

      <SectionCard icon={<CarOutlined />} title="Тээвэр">
        <Descriptions
          {...descriptionProps}
          items={[
            {
              key: 'car',
              label: 'Машины төрөл',
              children: isProcurement
                ? val(pricingDetail.find((item) => item.nameCode === pricingCode)?.name)
                : 'Өөрөө хүргэж өгөх',
            },
            {
              key: 'price',
              label: 'Нийт төлбөр (НӨАТ-гүй)',
              children: <Text strong>{formatCurrency(transportPrice)}</Text>,
            },
          ]}
        />
      </SectionCard>

      <SectionCard icon={<IoWarningOutline />} title="Анхаарах шаардлагатай зүйлс">
        <Descriptions
          {...descriptionProps}
          column={1}
          items={[
            {
              key: 'notes',
              label: 'Анхаарах шаардлагатай зүйлс',
              children:
                Array.isArray(importantNotes) && importantNotes.length
                  ? importantNotes.join(', ')
                  : '-',
            },
            {
              key: 'description',
              label: 'Тэмдэглэл',
              children: <div style={{ overflowWrap: 'break-word' }}>{val(description)}</div>,
            },
          ]}
        />
      </SectionCard>

      <StepFooter
        onPrev={prevStep}
        onSubmit={handleSubmit}
        htmlType="button"
        submitText="Татан авалт үүсгэх"
        loading={submitting}
        extra={
          <Text type="secondary">
            Тээврийн төлбөр: <Text strong>{formatCurrency(transportPrice)}</Text>
          </Text>
        }
      />
    </Space>
  );
};

export default Step3;
